import { Injectable } from '@angular/core';

import { createSelector, Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import type { AbilitiesMap } from '@recruitee/abilities-data';
import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { CurrentUserSelectors, UserSelectors } from '@recruitee/account-data';
import type { CurrentUser, User } from '@recruitee/account-types';
import { filterBoolean } from '@recruitee/common';
import { memoizeSelector } from '@recruitee/ngrx';
import { NoteSelectors } from '@recruitee/notes-data';
import type { Note } from '@recruitee/notes-types';
import type { Requisition } from '@recruitee/requisitions-types';

import { RootPageDataService } from './root-page-data.service';

const isLoaded = memoizeSelector((requisitionId: number) =>
  createSelector(
    NoteSelectors.requisitionScopeIsLoaded(requisitionId),
    UserSelectors.isLoaded(),
    CurrentUserSelectors.isLoaded,
    (a, b, c) => a && b && c,
  ),
);

@Injectable()
export class NotesPageDataService {
  public requisition$: Observable<Requisition>;
  public notes$: Observable<Note[]>;
  public users$: Observable<User[]>;
  public currentUser$: Observable<CurrentUser>;
  public abilities$: Observable<AbilitiesMap>;
  public isLoaded$: Observable<boolean>;

  constructor(
    private rootData: RootPageDataService,
    private store: Store,
  ) {
    this.requisition$ = rootData.requisition$;
    const requisitionId$ = this.requisition$.pipe(map(requisition => requisition.id));
    this.notes$ = requisitionId$.pipe(
      switchMap(id => store.select(NoteSelectors.requisitionScopeNested(id)).pipe(filterBoolean)),
    );
    this.users$ = store.select(UserSelectors.entities()).pipe(filterBoolean);
    this.currentUser$ = store.select(CurrentUserSelectors.data).pipe(filterBoolean);
    this.abilities$ = store.select(AbilitiesSelectors.abilityMap).pipe(filterBoolean);
    this.isLoaded$ = requisitionId$.pipe(switchMap(id => store.select(isLoaded(id))));
  }
}
